import { ReactComponent as UploadIcon } from 'assets/images/upload.svg';
import FormInput from 'components/FormInput';
import SearchSelect from 'components/SearchSelect';
import Compressor from 'compressorjs';
import { ProjectInfoSchema } from 'constants/ValidationSchema';
import { useFormik } from 'formik';
import { convertToBase64 } from 'helper/helper';
import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import isEqual from 'react-fast-compare';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ProjectInfo } from 'redux/projectSlice';
import { onSubmitParams } from './types';

interface ProjectInfoProps {
  onSubmitProject: (params: onSubmitParams) => void;
  projectDetails: ProjectInfo | null;
}

interface ProjectInfoValues {
  projectInfo: ProjectInfo;
}

const ProjectInformation = ({
  onSubmitProject,
  projectDetails,
}: ProjectInfoProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const categories = useSelector((state: any) => state.auth.categories);
  const [categoryOptions, setCategoryOptions] = useState<Array<object>>([
    { label: 'Add Category', value: 'Add category' },
  ]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const { setupId: projectId } = useParams();
  const [category, setCategory] = useState('');
  const initialValues: ProjectInfoValues = {
    projectInfo: {
      projectId: '',
      projectName: '',
      projectImg: '',
      category: '',
      location: '',
    },
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: ProjectInfoSchema,
    onSubmit: (values) => {
      const params = {
        values: values,
        draft: true,
        redirectAfterSubmit: true,
      };
      const isCompare = isEqual(values.projectInfo, projectDetails);
      if (!isCompare) {
        onSubmitProject(params);
      } else {
        navigate(`/project/setup/${projectId}/2`);
      }
    },
  });

  const compressImg = (image: any) => {
    new Compressor(image, {
      quality: 0.8, // 0.6 can also be used, but its not recommended to go below.
      success: async (compressedResult) => {
        // compressedResult has the compressed file.
        const base64Img = await convertToBase64(compressedResult);
        formik.setFieldValue('projectInfo.projectImg', base64Img);
      },
    });
  };

  useEffect(() => {
    const category = categories.map((item: any) => ({
      label: item,
      value: item,
    }));
    setCategoryOptions((prev) => [...prev, ...category]);
  }, [categories.length > 0]);

  useEffect(() => {
    if (projectDetails) {
      formik.setFieldValue('projectInfo', projectDetails);
    }
  }, [projectDetails]);

  const { errors, touched } = formik;
  return (
    <>
      <div className="project-info h-100 overflow-auto d-flex flex-column">
        <form
          className="h-100 overflow-auto d-flex flex-column"
          onSubmit={formik.handleSubmit}
        >
          <div className="step-wrapper flex-grow-1 overflow-auto">
            <div className="row">
              <div className="col-sm upload-img-wrapper mb-3">
                <div className="upload-img d-flex align-items-center text-center p-2 mx-auto">
                  {formik.values.projectInfo.projectImg ? (
                    <>
                      <img src={formik.values.projectInfo.projectImg} alt="" />
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        name="projectInfo.projectImg"
                        onChange={async (e: any) => {
                          compressImg(e.currentTarget.files[0]);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <span className="d-flex flex-column align-items-center">
                        <p className="mb-2">
                          <UploadIcon />
                        </p>
                        {t('label_upload_project_image')}
                      </span>
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        name="projectInfo.projectImg"
                        onChange={async (e: any) => {
                          compressImg(e.currentTarget.files[0]);
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="col-sm">
                <FormInput
                  label={t('label_project_id')}
                  name="projectInfo.projectId"
                  value={formik.values.projectInfo.projectId}
                  error={errors?.projectInfo?.projectId}
                  touched={touched?.projectInfo?.projectId}
                  onChange={formik.handleChange}
                />
                <FormInput
                  label={t('label_project_name')}
                  name="projectInfo.projectName"
                  value={formik.values.projectInfo.projectName}
                  error={errors?.projectInfo?.projectName}
                  touched={touched?.projectInfo?.projectName}
                  onChange={formik.handleChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-lg-6">
                <FormInput
                  label={t('label_site_location')}
                  name="projectInfo.location"
                  value={formik.values.projectInfo.location}
                  error={errors?.projectInfo?.location}
                  touched={touched?.projectInfo?.location}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="col-lg-6 mb-3">
                <Form.Label>
                  {t('label_category')} <span>*</span>
                </Form.Label>
                <SearchSelect
                  options={categoryOptions}
                  value={formik.values.projectInfo.category}
                  onChange={(val: any) => {
                    if (val.value === 'Add category') {
                      handleShow();
                    } else {
                      formik.setFieldValue('projectInfo.category', val.value);
                    }
                  }}
                  name="projectInfo.category"
                />
                {touched.projectInfo?.category && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: 'block' }}
                  >
                    {errors.projectInfo?.category}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex justify-space-between">
            <Link to="/projects" className="btn btn-outline-primary">
              {t('btn_back')}
            </Link>
            <Button variant="primary" type="submit" className="ms-auto">
              {t('btn_save-next')}
            </Button>
          </div>
        </form>
      </div>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header className="justify-content-center">
          <Modal.Title className="project-category-title">
            {t('modal_title_add_new_category')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Form.Label>
            {t('label_category_name')} <span>*</span>
          </Form.Label>
          <Form.Control
            name="category"
            value={category}
            onChange={(e: any) => setCategory(e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {t('feedback_label_category_name')}
          </Form.Control.Feedback>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0 pb-4">
          <Button
            variant="outline-secondary"
            className="min-w-100"
            onClick={handleClose}
          >
            {t('btn_cancel')}
          </Button>
          <Button
            variant="primary"
            className="min-w-100"
            onClick={() => {
              if (category) {
                const arr = [...categoryOptions];
                arr.push({
                  value: category.trim(),
                  label: category,
                });
                formik.setFieldValue('projectInfo.category', category);
                setCategoryOptions(arr);
                setCategory('');
              }
              handleClose();
            }}
          >
            {t('btn_add')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProjectInformation;
